import React, { useState, useContext, useEffect, useRef } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  Badge,
  Divider,
  Typography,
  Box,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import BugReportIcon from "@material-ui/icons/BugReport";
import WifiOffIcon from "@material-ui/icons/WifiOff";
import WifiIcon from "@material-ui/icons/Wifi";
import ClearAllIcon from "@material-ui/icons/ClearAll";
import AssessmentIcon from "@material-ui/icons/Assessment";
import ScheduleIcon from "@material-ui/icons/Schedule";
import { SocketManager } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  debugButton: {
    position: "fixed",
    zIndex: 9999,
    backgroundColor: theme.palette.error.main,
    color: "#fff",
    opacity: 0.2,
    transition: "opacity 0.2s ease-in-out",
    cursor: "grab",
    touchAction: "none",
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
      opacity: 1,
    },
    "&:active": {
      cursor: "grabbing",
    },
  },
  menuPaper: {
    maxWidth: 400,
    maxHeight: 600,
  },
  section: {
    padding: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  CONNECTED: {
    color: theme.palette.success.main,
  },
  DISCONNECTED: {
    color: theme.palette.error.main,
  },
  queueItem: {
    fontSize: "0.85rem",
    backgroundColor: theme.palette.background.default,
    marginBottom: theme.spacing(0.5),
    borderRadius: 4,
  },
  stats: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
}));

const DEBUG_BUTTON_POSITION_KEY = "debugMenuButtonPosition";
const DEBUG_BUTTON_SIZE = 48;
const DEFAULT_RIGHT = 20;
const DEFAULT_BOTTOM = 150;

const DebugMenu = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [disconnectDuration, setDisconnectDuration] = useState(5);
  const [queueStatus, setQueueStatus] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [socketReady, setSocketReady] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const dragActiveRef = useRef(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const dragStartRef = useRef({ x: 0, y: 0 });
  const hasDraggedRef = useRef(false);

  const clampPosition = (x, y) => {
    const maxX = Math.max(0, window.innerWidth - DEBUG_BUTTON_SIZE);
    const maxY = Math.max(0, window.innerHeight - DEBUG_BUTTON_SIZE);

    return {
      x: Math.min(Math.max(0, x), maxX),
      y: Math.min(Math.max(0, y), maxY),
    };
  };

  const getDefaultPosition = () => {
    const defaultX = window.innerWidth - DEBUG_BUTTON_SIZE - DEFAULT_RIGHT;
    const defaultY = window.innerHeight - DEBUG_BUTTON_SIZE - DEFAULT_BOTTOM;
    return clampPosition(defaultX, defaultY);
  };

  useEffect(() => {
    try {
      const saved = localStorage.getItem(DEBUG_BUTTON_POSITION_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (typeof parsed?.x === "number" && typeof parsed?.y === "number") {
          setButtonPosition(clampPosition(parsed.x, parsed.y));
          return;
        }
      }
    } catch (error) {
      // noop
    }

    setButtonPosition(getDefaultPosition());
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setButtonPosition((prev) => {
        if (prev.x === null || prev.y === null) {
          return getDefaultPosition();
        }
        return clampPosition(prev.x, prev.y);
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getClientPoint = (event) => {
    if (event.touches && event.touches.length > 0) {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
    return { x: event.clientX, y: event.clientY };
  };

  const persistPosition = (position) => {
    try {
      localStorage.setItem(DEBUG_BUTTON_POSITION_KEY, JSON.stringify(position));
    } catch (error) {
      // noop
    }
  };

  const handleDragMove = (event) => {
    if (!dragActiveRef.current) return;

    const point = getClientPoint(event);
    const dx = Math.abs(point.x - dragStartRef.current.x);
    const dy = Math.abs(point.y - dragStartRef.current.y);
    if (dx > 3 || dy > 3) {
      hasDraggedRef.current = true;
    }

    const next = clampPosition(
      point.x - dragOffsetRef.current.x,
      point.y - dragOffsetRef.current.y
    );
    setButtonPosition(next);
  };

  const handleDragEnd = () => {
    if (!dragActiveRef.current) return;
    dragActiveRef.current = false;
    setIsDragging(false);

    setButtonPosition((prev) => {
      if (prev.x !== null && prev.y !== null) {
        persistPosition(prev);
      }
      return prev;
    });

    window.removeEventListener("mousemove", handleDragMove);
    window.removeEventListener("mouseup", handleDragEnd);
    window.removeEventListener("touchmove", handleDragMove);
    window.removeEventListener("touchend", handleDragEnd);
  };

  const handleDragStart = (event) => {
    if (event.cancelable) {
      event.preventDefault();
    }

    const point = getClientPoint(event);
    const current = buttonPosition.x === null || buttonPosition.y === null
      ? getDefaultPosition()
      : buttonPosition;

    dragOffsetRef.current = {
      x: point.x - current.x,
      y: point.y - current.y,
    };
    dragStartRef.current = { x: point.x, y: point.y };
    hasDraggedRef.current = false;

    dragActiveRef.current = true;
    setIsDragging(true);

    window.addEventListener("mousemove", handleDragMove);
    window.addEventListener("mouseup", handleDragEnd);
    window.addEventListener("touchmove", handleDragMove, { passive: false });
    window.addEventListener("touchend", handleDragEnd);
  };

  const handleDebugButtonClick = (event) => {
    if (hasDraggedRef.current) {
      hasDraggedRef.current = false;
      return;
    }
    handleClick(event);
  };

  useEffect(() => {
    // Apenas executar se for usuário ID 1
    if (!user || user.id !== 1) return;

    const interval = setInterval(() => {
      try {
        // Acessar SocketManager diretamente (importado)
        const companyId = localStorage.getItem("companyId");
        if (!companyId) {
          setIsConnected(false);
          setSocketReady(false);
          return;
        }

        // SocketManager.currentSocket é o socket real (rawSocket do socket.io)
        if (SocketManager && SocketManager.currentSocket) {
          const rawSocket = SocketManager.currentSocket;
          setIsConnected(rawSocket.CONNECTED || false);
          setSocketReady(SocketManager.socketReady || false);
        } else {
          setIsConnected(false);
          setSocketReady(false);
        }
      } catch (error) {
        setIsConnected(false);
        setSocketReady(false);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [user]);

  // Apenas mostrar para usuário ID 1
  if (!user || user.id !== 1) {
    return null;
  }

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getSocket = () => {
    try {
      // Retorna o rawSocket (socket.io real) do SocketManager
      return SocketManager?.currentSocket || null;
    } catch (error) {
      console.error("Erro ao obter socket:", error);
    }
    return null;
  };

  const handleDisconnect = () => {
    const socket = getSocket();
    if (socket && typeof socket.disconnect === "function") {
      socket.disconnect();
      toast.info("🔌 WebSocket desconectado manualmente");
      handleClose();
    } else {
      toast.error("Socket não disponível");
    }
  };

  const handleReconnect = () => {
    const socket = getSocket();
    if (socket && typeof socket.connect === "function") {
      socket.connect();
      toast.success("🔄 Reconectando WebSocket...");
      handleClose();
    } else {
      toast.error("Socket não disponível");
    }
  };

  const handleSimulateDisconnect = () => {
    setDisconnectDialogOpen(true);
    handleClose();
  };

  const confirmSimulateDisconnect = () => {
    const socket = getSocket();
    if (socket && typeof socket.disconnect === "function") {
      socket.disconnect();
      toast.warning(`🧪 Simulando desconexão por ${disconnectDuration} segundos...`);
      
      setTimeout(() => {
        if (socket && typeof socket.connect === "function") {
          socket.connect();
          toast.info("🔄 Reconectando após simulação...");
        }
      }, disconnectDuration * 1000);
    } else {
      toast.error("Socket não disponível");
    }
    setDisconnectDialogOpen(false);
  };

  const handleClearCache = () => {
    // Limpar localStorage relacionado a cache
    toast.success("🗑️ Cache local limpo");
    handleClose();
  };

  const handleShowStats = () => {
    setStatsDialogOpen(true);
    handleClose();
  };

  const open = Boolean(anchorEl);

  return (
    <>
      {/* Botão de Debug Flutuante */}
      <IconButton
        className={classes.debugButton}
        onClick={handleDebugButtonClick}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        style={{
          left: buttonPosition.x !== null ? buttonPosition.x : undefined,
          top: buttonPosition.y !== null ? buttonPosition.y : undefined,
          opacity: isDragging ? 1 : undefined,
        }}
        title="Menu de Debug (Usuário 1)"
      >
        <Badge badgeContent={queueStatus?.queueSize || 0} color="secondary">
          <BugReportIcon />
        </Badge>
      </IconButton>

      {/* Menu Principal */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        classes={{ paper: classes.menuPaper }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            🐛 Menu de Debug
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Apenas visível para usuário ID 1
          </Typography>
        </Box>

        <Divider />

        {/* Status do WebSocket Backend */}
        <Box className={classes.section}>
          <Typography variant="subtitle2" className={classes.sectionTitle}>
            WebSocket (Frontend ↔ Backend)
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
            Comunicação em tempo real entre navegador e servidor
          </Typography>
          <Box className={classes.stats}>
            <Chip
              icon={isConnected ? <WifiIcon /> : <WifiOffIcon />}
              label={isConnected ? "WebSocket Conectado" : "WebSocket Desconectado"}
              size="small"
              color={isConnected ? "primary" : "default"}
              className={isConnected ? classes.CONNECTED : classes.DISCONNECTED}
            />
            <Chip
              label={socketReady ? "Sistema Pronto" : "Inicializando..."}
              size="small"
              color={socketReady ? "primary" : "default"}
            />
          </Box>
        </Box>

        <Divider />

        {/* Ações WebSocket */}
        <MenuItem onClick={handleDisconnect} disabled={!isConnected}>
          <WifiOffIcon style={{ marginRight: 8 }} />
          Desconectar WebSocket do Backend
        </MenuItem>
        <MenuItem onClick={handleReconnect} disabled={isConnected}>
          <WifiIcon style={{ marginRight: 8 }} />
          Reconectar WebSocket ao Backend
        </MenuItem>
        <MenuItem onClick={handleSimulateDisconnect} disabled={!isConnected}>
          <ScheduleIcon style={{ marginRight: 8 }} />
          Simular Desconexão Temporária
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleShowStats}>
          <AssessmentIcon style={{ marginRight: 8 }} />
          Ver Estatísticas Detalhadas
        </MenuItem>
        <MenuItem onClick={handleClearCache}>
          <ClearAllIcon style={{ marginRight: 8 }} />
          Limpar Cache Local
        </MenuItem>
      </Menu>

      {/* Dialog: Simular Desconexão */}
      <Dialog open={disconnectDialogOpen} onClose={() => setDisconnectDialogOpen(false)}>
        <DialogTitle>Simular Desconexão Temporária</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" paragraph>
            O WebSocket será desconectado e reconectado automaticamente após o tempo especificado.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Duração (segundos)"
            type="number"
            fullWidth
            value={disconnectDuration}
            onChange={(e) => setDisconnectDuration(parseInt(e.target.value) || 5)}
            inputProps={{ min: 1, max: 300 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialogOpen(false)}>Cancelar</Button>
          <Button onClick={confirmSimulateDisconnect} color="primary">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Estatísticas */}
      <Dialog 
        open={statsDialogOpen} 
        onClose={() => setStatsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>📊 Estatísticas de WebSocket (Frontend ↔ Backend)</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Status da Conexão WebSocket
            </Typography>
            <Box className={classes.stats}>
              <Chip 
                label={isConnected ? "✅ WebSocket Conectado" : "❌ WebSocket Desconectado"} 
                color={isConnected ? "primary" : "default"}
              />
              <Chip 
                label={socketReady ? "✅ Sistema Pronto" : "⚠️ Inicializando"} 
                color={socketReady ? "primary" : "default"}
              />
            </Box>
          </Box>

          <Box mb={2}>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              ℹ️ O que é cada conexão?
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>WebSocket (Frontend ↔ Backend):</strong> Conexão em tempo real entre seu navegador e o servidor Node.js. Usado para receber notificações de tickets, mensagens, etc.
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>Conexões WhatsApp:</strong> São gerenciadas pelo backend e aparecem na página de "Conexões". São independentes do WebSocket.
            </Typography>
            <Typography variant="body2">
              • <strong>Backend:</strong> O servidor Node.js que processa as requisições e gerencia as conexões WhatsApp.
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Dicas de Debug
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>F12</strong> → Console: veja mensagens de conexão/desconexão do WebSocket
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>F12</strong> → Network → WS: inspecione tráfego WebSocket em tempo real
            </Typography>
            <Typography variant="body2">
              • O sistema reconecta automaticamente em caso de desconexão
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatsDialogOpen(false)} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DebugMenu;
